/**
 * Category constants for unified integrations.
 */
export const Category = {
  /**
   * The ATS category.
   */
  ATS: 'ats'
} as const;

/**
 * Type for Category constants.
 */
export type CategoryType = typeof Category[keyof typeof Category];

/**
 * Interface representing a unified integration.
 */
export interface IUnifiedIntegration {
  id: number;
  name: string;
  description?: string;
  logo?: string;
  type: string;
  enabled: boolean;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Interface representing a user's unified integration.
 */
export interface IUserUnifiedIntegration {
  id: number;
  user_id: number;
  unified_integration_id: number;
  enabled: boolean;
  connection_id: string;
  createdAt: Date;
  updatedAt: Date;
}

export type ClosedEnum<T> = T[keyof T];

export const Origin = {
  Agency: "AGENCY",
  Applied: "APPLIED",
  Internal: "INTERNAL",
  Referred: "REFERRED",
  Sourced: "SOURCED",
  University: "UNIVERSITY",
} as const;
export type Origin = ClosedEnum<typeof Origin>;

export type PropertyAtsCandidateAddress = {
  address1?: string | undefined;
  address2?: string | undefined;
  city?: string | undefined;
  country?: string | undefined;
  countryCode?: string | undefined;
  postalCode?: string | undefined;
  region?: string | undefined;
  regionCode?: string | undefined;
};


export const AtsEmailType = {
  Work: "WORK",
  Home: "HOME",
  Other: "OTHER",
} as const;
export type AtsEmailType = ClosedEnum<typeof AtsEmailType>;


export const AtsTelephoneType = {
  Work: "WORK",
  Home: "HOME",
  Other: "OTHER",
  Fax: "FAX",
  Mobile: "MOBILE",
} as const;
export type AtsTelephoneType = ClosedEnum<typeof AtsTelephoneType>;

export type AtsTelephone = {
  telephone: string;
  type?: AtsTelephoneType | undefined;
};

export type AtsEmail = {
  email: string;
  name?: string | undefined;
  type?: AtsEmailType | undefined;
};

/**
 * Interface representing a candidate in the ATS.
 */
export interface AtsCandidate {
  address?: PropertyAtsCandidateAddress | undefined;
  companyId?: string | undefined;
  companyName?: string | undefined;
  createdAt?: Date | undefined;
  dateOfBirth?: Date | undefined;
  emails?: Array<AtsEmail> | undefined;
  externalIdentifier?: string | undefined;
  id?: string | undefined;
  imageUrl?: string | undefined;
  /**
   * URLs for web pages containing additional material about the candidate (LinkedIn, other social media, articles, etc.)
   */
  linkUrls?: Array<string> | undefined;
  name?: string | undefined;
  origin?: Origin | undefined;
  raw?: { [k: string]: any } | undefined;
  sources?: Array<string> | undefined;
  tags?: Array<string> | undefined;
  telephones?: Array<AtsTelephone> | undefined;
  title?: string | undefined;
  updatedAt?: Date | undefined;
  userId?: string | undefined;
  webUrl?: string | undefined;
}

/**
 * Type representing an application in the ATS.
 */
export type AtsApplication = {
	id?: string;
	candidate_id?: string;
	job_id?: string;
	created_at?: string; // ISO date
	updated_at?: string; // ISO date
	applied_at?: string; // ISO date
	hired_at?: string; // ISO date
	rejected_at?: string; // ISO date
	rejected_reason?: string;
	source?: string;
	status?: 'NEW' | 'REVIEWING' | 'SCREENING' | 'SUBMITTED' | 'FIRST_INTERVIEW' | 'SECOND_INTERVIEW' | 'THIRD_INTERVIEW' | 'BACKGROUND_CHECK' | 'OFFERED' | 'ACCEPTED' | 'HIRED' | 'REJECTED' | 'DECLINED' | 'WITHDRAWN';
	original_status?: string; // The integration's original status.  Mostly used to have your customers chose the application status/stage so that your software can trigger an event.
	answers?: {
		question_id?: string;
		answers?: any;
	}[];
	offers?: Record<string, any>[];
}

/**
 * Type representing a job in the ATS.
 */
export type AtsJob = {
	id?: string;
	created_at?: string; // ISO date
	updated_at?: string; // ISO date
	name?: string;
	description?: string;
	recruiter_ids?: string[];
	hiring_manager_ids?: string[];
	status?: 'ARCHIVED' | 'PENDING' | 'DRAFT' | 'OPEN' | 'CLOSED';
	closed_at?: string; // ISO date
	addresses?: {
		address1?: string;
		address2?: string;
		city?: string;
		region?: string; // Regional area of the employee's address.  For example, in the U.S., the region is the employee's state; in Canada, the region is the employee’s province.
		region_code?: string; // Short form for the regional area of the employee's address.   For example, in the U.S., the region code is the two-letter abbreviation for the employee’s state; in Canada, the region is the two-letter abbreviation for the employee's province.
		postal_code?: string;
		country?: string;
		country_code?: string; // Country code for the country, in ISO 3166 A-2 format
	}[];
	compensation?: {
		type?: 'SALARY' | 'BONUS' | 'STOCK_OPTIONS' | 'EQUITY' | 'OTHER';
		min?: number;
		max?: number;
		currency?: string;
		frequency?: 'ONE_TIME' | 'DAY' | 'QUARTER' | 'YEAR' | 'HOUR' | 'MONTH' | 'WEEK';
	}[];
	employment_type?: 'FULL_TIME' | 'PART_TIME' | 'CONTRACTOR' | 'INTERN' | 'CONSULTANT' | 'VOLUNTEER' | 'CASUAL' | 'SEASONAL' | 'FREELANCE' | 'OTHER';
	remote?: boolean;
	departments?: string[]; // The names of the departments/divisions that this job belongs to
	language_locale?: string; // Preferred language for the job, in ISO 639-1 format (e.g., U.S. English is en-us)
	public_job_urls?: string[]; // URLs for pages containing public listings for the job
	number_of_openings?: number; // Number of openings for the job
	company_id?: string; // id value of the company associated with the job in the ATS
	questions?: {
		id?: string;
		question?: string;
		required?: boolean;
		description?: string;
		prompt?: string;
		type?: 'TEXT' | 'NUMBER' | 'DATE' | 'BOOLEAN' | 'MULTIPLE_CHOICE' | 'FILE' | 'TEXTAREA' | 'MULTIPLE_SELECT' | 'UNIVERSITY' | 'YES_NO' | 'CURRENCY' | 'URL';
		options?: any;
	}[];
}
